// src/pages/Scheduler.jsx
import React, { useState } from 'react';
import { 
  Calendar, 
  Plus, 
  Clock, 
  Edit, 
  Trash2, 
  Eye, 
  ChevronLeft, 
  ChevronRight,
  Play,
  Pause,
  AlertCircle
} from 'lucide-react';

const Scheduler = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'week'
  const [selectedDate, setSelectedDate] = useState(null);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [previewDateTime, setPreviewDateTime] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5)
  });

  // Données de démonstration
  const scheduleRules = [
    {
      id: 1,
      name: 'Menu Petit-déjeuner',
      description: 'Affichage du menu petit-déjeuner',
      template: 'template-breakfast',
      timeRange: '06:00-11:00',
      daysOfWeek: [1, 2, 3, 4, 5, 6], // Lundi à Samedi
      startDate: '2025-06-01',
      endDate: null,
      priority: 1,
      isActive: true,
      tags: { 'price_petit_dej': '2€80', 'opening_hours': 'DE 6H00 A 11H00 DU LUNDI AU SAMEDI' }
    },
    {
      id: 2,
      name: 'Prix Weekend',
      description: 'Prix spéciaux pour le weekend',
      template: 'template-breakfast',
      timeRange: '06:00-11:00',
      daysOfWeek: [6, 0], // Samedi et Dimanche
      startDate: '2025-06-01',
      endDate: null,
      priority: 2,
      isActive: true,
      tags: { 'price_petit_dej': '3€20', 'special_offer': 'Prix weekend' }
    },
    {
      id: 3,
      name: 'Menu Noël',
      description: 'Menu spécial période de Noël',
      template: 'template-christmas',
      timeRange: '00:00-23:59',
      daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
      startDate: '2025-12-20',
      endDate: '2025-12-25',
      priority: 3,
      isActive: false,
      tags: { 'special_offer': 'Menu de Noël 15€', 'season': 'christmas' }
    }
  ];

  // Navigation du calendrier
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  // Obtenir les jours du mois
  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay() + 1); // Commencer par lundi

    const days = [];
    for (let i = 0; i < 42; i++) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      days.push(day);
    }
    return days;
  };

  // Vérifier si une règle s'applique à une date
  const getRulesForDate = (date) => {
    const dayOfWeek = date.getDay();
    const dateStr = date.toISOString().split('T')[0];
    
    return scheduleRules.filter(rule => {
      if (!rule.isActive) return false;
      
      // Vérifier le jour de la semaine
      if (!rule.daysOfWeek.includes(dayOfWeek)) return false;
      
      // Vérifier la période
      if (rule.startDate && dateStr < rule.startDate) return false;
      if (rule.endDate && dateStr > rule.endDate) return false;
      
      return true;
    });
  };

  // Formulaire de règle
  const RuleForm = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Nouvelle Règle de Planification</h2>
          <button
            onClick={() => setShowRuleForm(false)}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        <form className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nom de la règle
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Ex: Menu du midi"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Template
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                <option>Menu Petit-déjeuner</option>
                <option>Menu Déjeuner</option>
                <option>Promotions</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Description de la règle..."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Heure de début
              </label>
              <input
                type="time"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Heure de fin
              </label>
              <input
                type="time"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Jours de la semaine
            </label>
            <div className="flex space-x-2">
              {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((day, index) => (
                <label key={day} className="flex items-center">
                  <input
                    type="checkbox"
                    className="mr-1 rounded"
                    defaultChecked={index < 6}
                  />
                  <span className="text-sm">{day}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de début
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date de fin (optionnel)
              </label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Priorité
            </label>
            <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
              <option value="1">1 - Très faible</option>
              <option value="2">2 - Faible</option>
              <option value="3">3 - Normale</option>
              <option value="4">4 - Élevée</option>
              <option value="5">5 - Très élevée</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => setShowRuleForm(false)}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Créer la règle
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Planificateur de Contenu</h1>
          <p className="text-gray-600">Gérez la programmation de vos affichages</p>
        </div>
        
        <button
          onClick={() => setShowRuleForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Nouvelle Règle</span>
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Calendrier */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-lg shadow border">
            {/* En-tête du calendrier */}
            <div className="p-6 border-b">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold">
                  {currentDate.toLocaleDateString('fr-FR', { 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => navigateMonth(-1)}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setCurrentDate(new Date())}
                    className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded"
                  >
                    Aujourd'hui
                  </button>
                  <button
                    onClick={() => navigateMonth(1)}
                    className="p-2 hover:bg-gray-100 rounded"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Grille du calendrier */}
            <div className="p-6">
              {/* En-têtes des jours */}
              <div className="grid grid-cols-7 gap-2 mb-4">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(day => (
                  <div key={day} className="text-center text-sm font-medium text-gray-600 p-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Jours du mois */}
              <div className="grid grid-cols-7 gap-2">
                {getDaysInMonth().map((day, index) => {
                  const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                  const isToday = day.toDateString() === new Date().toDateString();
                  const rules = getRulesForDate(day);
                  
                  return (
                    <div
                      key={index}
                      className={`min-h-[80px] border rounded p-2 cursor-pointer transition-colors ${
                        isCurrentMonth 
                          ? 'bg-white hover:bg-gray-50' 
                          : 'bg-gray-50 text-gray-400'
                      } ${isToday ? 'ring-2 ring-blue-500' : ''}`}
                      onClick={() => setSelectedDate(day)}
                    >
                      <div className="text-sm font-medium mb-1">
                        {day.getDate()}
                      </div>
                      
                      {/* Indicateurs de règles */}
                      <div className="space-y-1">
                        {rules.slice(0, 2).map(rule => (
                          <div
                            key={rule.id}
                            className={`text-xs px-1 py-0.5 rounded truncate ${
                              rule.priority === 1 ? 'bg-blue-100 text-blue-700' :
                              rule.priority === 2 ? 'bg-green-100 text-green-700' :
                              'bg-purple-100 text-purple-700'
                            }`}
                            title={rule.name}
                          >
                            {rule.name}
                          </div>
                        ))}
                        {rules.length > 2 && (
                          <div className="text-xs text-gray-500">
                            +{rules.length - 2} autres
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Panneau latéral */}
        <div className="space-y-6">
          {/* Règles actives */}
          <div className="bg-white rounded-lg shadow border">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Règles Programmées</h3>
            </div>
            <div className="p-4 space-y-3">
              {scheduleRules.map(rule => (
                <div key={rule.id} className="border rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-medium text-sm">{rule.name}</h4>
                    <div className="flex items-center space-x-1">
                      <div className={`w-2 h-2 rounded-full ${
                        rule.isActive ? 'bg-green-500' : 'bg-gray-300'
                      }`} />
                      <button className="text-gray-400 hover:text-gray-600">
                        <Edit className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                  <div className="text-xs text-gray-600 space-y-1">
                    <div className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>{rule.timeRange}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`px-1 py-0.5 rounded text-xs ${
                        rule.priority <= 2 ? 'bg-blue-100 text-blue-700' :
                        rule.priority <= 4 ? 'bg-orange-100 text-orange-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        Priorité {rule.priority}
                      </span>
                      <span className={`px-1 py-0.5 rounded text-xs ${
                        rule.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {rule.isActive ? 'Actif' : 'Inactif'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Prévisualisation temporelle */}
          <div className="bg-white rounded-lg shadow border">
            <div className="p-4 border-b">
              <h3 className="font-semibold">Prévisualisation Temporelle</h3>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={previewDateTime.date}
                  onChange={(e) => setPreviewDateTime(prev => ({ ...prev, date: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Heure
                </label>
                <input
                  type="time"
                  value={previewDateTime.time}
                  onChange={(e) => setPreviewDateTime(prev => ({ ...prev, time: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>

              <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2">
                <Eye className="h-4 w-4" />
                <span>Prévisualiser</span>
              </button>

              {/* Résultat de la prévisualisation */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm font-medium text-gray-700 mb-2">
                  Contenu affiché le {new Date(previewDateTime.date).toLocaleDateString('fr-FR')} à {previewDateTime.time}
                </div>
                <div className="bg-gray-800 text-white p-3 rounded text-center text-sm">
                  <div className="text-yellow-400 font-bold mb-1">Les P'tits dej de la Grange</div>
                  <div className="text-xs mb-2">DE 6H00 A 11H00 DU LUNDI AU SAMEDI</div>
                  <div className="flex justify-between items-center text-xs">
                    <span>LE PETIT P'TIT DEJ</span>
                    <span className="text-yellow-400 font-bold">2€80</span>
                  </div>
                </div>
                <div className="text-xs text-gray-600 mt-2">
                  Règle active: Menu Petit-déjeuner (Priorité 1)
                </div>
              </div>
            </div>
          </div>

          {/* Conflits et alertes */}
          <div className="bg-white rounded-lg shadow border">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center space-x-2">
                <AlertCircle className="h-4 w-4 text-orange-500" />
                <span>Alertes</span>
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-start space-x-3 p-3 bg-orange-50 rounded-lg">
                <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5" />
                <div className="text-sm">
                  <div className="font-medium text-orange-800">Conflit de priorité</div>
                  <div className="text-orange-700">
                    "Menu Petit-déjeuner" et "Prix Weekend" se chevauchent le samedi
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5" />
                <div className="text-sm">
                  <div className="font-medium text-blue-800">Règle inactive</div>
                  <div className="text-blue-700">
                    "Menu Noël" est programmé mais actuellement désactivé
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Détails de la date sélectionnée */}
      {selectedDate && (
        <div className="bg-white rounded-lg shadow border p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">
              Programmation du {selectedDate.toLocaleDateString('fr-FR', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </h3>
            <button
              onClick={() => setSelectedDate(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getRulesForDate(selectedDate).map(rule => (
              <div key={rule.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-medium">{rule.name}</h4>
                    <p className="text-sm text-gray-600">{rule.description}</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Play className="h-3 w-3 text-green-600" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Edit className="h-3 w-3 text-gray-600" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Trash2 className="h-3 w-3 text-red-600" />
                    </button>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Horaire:</span>
                    <span className="font-medium">{rule.timeRange}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Priorité:</span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      rule.priority <= 2 ? 'bg-blue-100 text-blue-700' :
                      rule.priority <= 4 ? 'bg-orange-100 text-orange-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {rule.priority}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      rule.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {rule.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </div>
                </div>

                {/* Tags personnalisés */}
                {rule.tags && Object.keys(rule.tags).length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <div className="text-xs text-gray-600 mb-1">Contenu dynamique:</div>
                    <div className="space-y-1">
                      {Object.entries(rule.tags).map(([key, value]) => (
                        <div key={key} className="text-xs bg-gray-100 rounded px-2 py-1">
                          <span className="font-medium">{key}:</span> {value}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {getRulesForDate(selectedDate).length === 0 && (
              <div className="col-span-full text-center py-8 text-gray-500">
                <Calendar className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>Aucune règle programmée pour cette date</p>
                <button
                  onClick={() => setShowRuleForm(true)}
                  className="mt-2 text-blue-600 hover:text-blue-700 text-sm"
                >
                  Créer une nouvelle règle
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Formulaire de création de règle */}
      {showRuleForm && <RuleForm />}
    </div>
  );
};

export default Scheduler;